import { useState, useEffect, useCallback } from 'react';
import { useEasykitPlanningStore } from '@/stores/easykitPlanning';
import {
  stripHtml,
  formatDutchDate,
  EASYKIT_TAGS,
} from '@/utils/easykit';
import { useEasykitTaskDisplay } from './useEasykitTaskDisplay';
import { EasykitDetailHeader } from './EasykitDetailHeader';
import type { OdooTask } from '@/types/odoo';


interface EasykitTaskDetailPanelProps {
  task: OdooTask;
}

export function EasykitTaskDetailPanel({ task }: EasykitTaskDetailPanelProps) {
  const updateTask = useEasykitPlanningStore((s) => s.updateTask);
  const toggleTag = useEasykitPlanningStore((s) => s.toggleTag);

  const [editing, setEditing] = useState(false);
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);

  const rawDescription = typeof task.description === 'string' ? stripHtml(task.description) : '';

  useEffect(() => {
    setDescription(rawDescription);
    setEditing(false);
  }, [task.id, rawDescription]);

  const handleSave = useCallback(async () => {
    if (description === rawDescription) { setEditing(false); return; }
    setSaving(true);
    try {
      await updateTask(task.id, { description });
      setEditing(false);
    } catch {
      // Store sets error banner; keep editing open so user can retry.
    } finally {
      setSaving(false);
    }
  }, [task.id, description, rawDescription, updateTask]);

  const handleCancel = useCallback(() => {
    setDescription(rawDescription);
    setEditing(false);
  }, [rawDescription]);

  const { projectName, address, soSummary } = useEasykitTaskDisplay(task);
  const displayProjectName = projectName || 'Onbekend project';

  const dateStr = typeof task.date_deadline === 'string' ? task.date_deadline.slice(0, 10) : '';
  const formattedDate = dateStr ? formatDutchDate(dateStr) : '';

  const tagIds = task.tag_ids ?? [];

  return (
    <div className="px-4 lg:px-8 mt-4">
      <div className="border rounded-lg bg-white shadow-sm">
        <EasykitDetailHeader task={task} />

        {/* Detail content */}
        <div className="p-4 space-y-3">
          {/* Project */}
          <p className="text-sm text-td-navy">{displayProjectName}</p>

          {/* Collector breakdown — visible whenever the SO has materialen lines */}
          {soSummary && soSummary.breakdown.length > 0 && (
            <div className="rounded-lg border border-gray-200 bg-gray-50 overflow-hidden">
              <table className="w-full text-xs">
                <thead className="bg-gray-100 text-gray-500">
                  <tr>
                    <th className="text-left px-3 py-1.5 font-semibold">Systeem</th>
                    <th className="text-left px-3 py-1.5 font-semibold">Familie</th>
                    <th className="text-left px-3 py-1.5 font-semibold">LP</th>
                    <th className="text-right px-3 py-1.5 font-semibold">m²</th>
                  </tr>
                </thead>
                <tbody>
                  {soSummary.breakdown.map((c, i) => (
                    <tr key={i} className="border-t border-gray-200">
                      <td className="px-3 py-1.5 font-medium text-td-navy">{c.systeem}</td>
                      <td className="px-3 py-1.5 text-td-muted">{c.family}</td>
                      <td className="px-3 py-1.5 text-td-muted">{c.lp}</td>
                      <td className="px-3 py-1.5 text-right font-medium text-td-navy">{c.m2}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Datum */}
          {formattedDate && (
            <p className="text-sm text-td-navy">
              <span className="font-semibold">Datum:</span> {formattedDate}
            </p>
          )}

          {/* Adres */}
          {address && (
            <p className="text-sm text-td-navy">
              <span className="font-semibold">Adres:</span> {address}
            </p>
          )}

          {/* Tag toggles */}
          <div className="flex flex-wrap gap-2">
            {EASYKIT_TAGS.map((tag) => {
              const active = tagIds.includes(tag.id);
              return (
                <button
                  key={tag.id}
                  type="button"
                  onClick={() => toggleTag(task.id, tag.id, active ? 'remove' : 'add')}
                  className={`px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
                    active ? tag.toggled : tag.untoggled
                  }`}
                >
                  {tag.label}
                </button>
              );
            })}
          </div>

          {/* Omschrijving */}
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-sm font-semibold text-td-navy">Omschrijving</span>
              {!editing && (
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  className="text-xs text-td-blue hover:underline"
                >
                  Bewerken
                </button>
              )}
            </div>
            {editing ? (
              <div className="space-y-2">
                <textarea
                  id="ek-task-description"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  rows={4}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm resize-y focus:outline-none focus:ring-2 focus:ring-td-blue/30 focus:border-td-blue"
                  placeholder="Opmerking toevoegen..."
                />
                <div className="flex gap-2">
                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={saving}
                    className="px-3 py-1.5 text-xs font-semibold text-white bg-td-blue rounded-lg hover:bg-td-blue/90 transition-colors disabled:opacity-50"
                  >
                    {saving ? 'Opslaan...' : 'Opslaan'}
                  </button>
                  <button
                    type="button"
                    onClick={handleCancel}
                    disabled={saving}
                    className="px-3 py-1.5 text-xs font-semibold text-gray-600 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
                  >
                    Annuleren
                  </button>
                </div>
              </div>
            ) : (
              <p className="text-sm text-td-muted whitespace-pre-wrap">
                {rawDescription || 'Geen omschrijving'}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
